import { type FC, useCallback, useState } from "react";

import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { useOrganization } from "../../hooks/useOrganization";
import { isAdmin } from "../../utils/rbac";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { EmptyState } from "../common/EmptyState";
import { FormInput } from "../common/forms/FormInput";
import { FormSelect } from "../common/forms/FormSelect";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";
import {
  OrganizationPendingInvitesSection,
  type PendingInviteAction,
} from "./OrganizationPendingInvitesSection";

const ROLE_OPTIONS = [
  { label: "member", value: "member" },
  { label: "admin", value: "admin" },
  { label: "owner", value: "owner" },
] as const;

const OrganizationMemberRow: FC<{
  canManage: boolean;
  member: {
    email: string | null;
    id: string;
    name: string | null;
    role: string;
  };
  onRemove: (memberId: string) => void;
  onRoleChange: (memberId: string, role: string) => void;
}> = ({ canManage, member, onRemove, onRoleChange }) => (
  <DataTable.Row>
    <DataTable.Cell className="text-gray-900 dark:text-white">
      {member.email ?? "\u2014"}
    </DataTable.Cell>
    <DataTable.Cell className="text-gray-700 dark:text-gray-300">
      {member.name ?? "\u2014"}
    </DataTable.Cell>
    <DataTable.Cell>
      {canManage ? (
        <FormSelect
          size="sm"
          value={member.role}
          onChange={(e) => onRoleChange(member.id, e.target.value)}
          options={[...ROLE_OPTIONS]}
        />
      ) : (
        <span className="capitalize text-gray-700 dark:text-gray-300">
          {member.role}
        </span>
      )}
    </DataTable.Cell>
    {canManage ? (
      <DataTable.Cell>
        <Button variant="danger" size="sm" onClick={() => onRemove(member.id)}>
          Remove
        </Button>
      </DataTable.Cell>
    ) : null}
  </DataTable.Row>
);

export const OrganizationPanel: FC = () => {
  const auth = useDashboardAuth();
  const {
    org,
    members,
    invites,
    loading,
    error: organizationError,
    inviteMember,
    invalidateInvite,
    changeMemberRole,
    deleteMember,
  } = useOrganization(auth.organizationId);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [inviteActionState, setInviteActionState] = useState<{
    inviteId: string;
    action: PendingInviteAction;
  } | null>(null);

  const canManage = isAdmin(auth.orgRole, auth.globalRole);
  const pendingInvites = invites.filter(
    (invite) => invite.status === "pending",
  );

  const handleInvite = useCallback(async () => {
    if (!inviteEmail.trim()) {
      return;
    }
    setInviting(true);
    setActionError(null);
    try {
      await inviteMember(inviteEmail.trim(), inviteRole);
      setInviteEmail("");
      setInviteRole("member");
    } catch (caughtError) {
      setActionError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to invite member",
      );
    } finally {
      setInviting(false);
    }
  }, [inviteEmail, inviteRole, inviteMember]);

  const handleRoleChange = useCallback(
    async (memberId: string, role: string) => {
      setActionError(null);
      try {
        await changeMemberRole(memberId, role);
      } catch (caughtError) {
        setActionError(
          caughtError instanceof Error
            ? caughtError.message
            : "Failed to update role",
        );
      }
    },
    [changeMemberRole],
  );

  const handleRemove = useCallback(
    async (memberId: string) => {
      setActionError(null);
      try {
        await deleteMember(memberId);
      } catch (caughtError) {
        setActionError(
          caughtError instanceof Error
            ? caughtError.message
            : "Failed to remove member",
        );
      }
    },
    [deleteMember],
  );

  const handleInvalidateInvite = useCallback(
    async (inviteId: string) => {
      setActionError(null);
      setInviteActionState({ action: "invalidate", inviteId });
      try {
        await invalidateInvite(inviteId);
      } catch (caughtError) {
        setActionError(
          caughtError instanceof Error
            ? caughtError.message
            : "Failed to invalidate invite",
        );
      } finally {
        setInviteActionState(null);
      }
    },
    [invalidateInvite],
  );

  const handleResendInvite = useCallback(
    async (inviteId: string, email: string, role: string) => {
      setActionError(null);
      setInviteActionState({ action: "resend", inviteId });
      try {
        await invalidateInvite(inviteId);
        await inviteMember(email, role);
      } catch (caughtError) {
        setActionError(
          caughtError instanceof Error
            ? caughtError.message
            : "Failed to resend invite",
        );
      } finally {
        setInviteActionState(null);
      }
    },
    [invalidateInvite, inviteMember],
  );

  if (!auth.organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Select an organization to manage its settings and members."
      />
    );
  }

  if (loading) {
    return <LoadingState message="Loading organization..." />;
  }

  if (organizationError) {
    return <Alert variant="error">{organizationError}</Alert>;
  }

  return (
    <div className="space-y-4">
      {actionError && <Alert variant="error">{actionError}</Alert>}

      {/* Org info */}
      {org && (
        <PanelSection title="Organization Info">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Name</span>
              <p className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
                {org.name}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Slug</span>
              <p className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
                {org.slug}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Plan</span>
              <p className="font-mono text-[11px] font-bold capitalize text-slate-900 dark:text-white">
                {org.plan_tier}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Status</span>
              <p className="font-mono text-[11px] font-bold capitalize text-slate-900 dark:text-white">
                {org.status}
              </p>
            </div>
          </div>
        </PanelSection>
      )}

      {/* Members */}
      <PanelSection title="Members">
        {members.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            No members found.
          </p>
        ) : (
          <DataTable responsive>
            <DataTable.Header>
              <tr>
                <DataTable.HeaderCell>Email</DataTable.HeaderCell>
                <DataTable.HeaderCell>Name</DataTable.HeaderCell>
                <DataTable.HeaderCell>Role</DataTable.HeaderCell>
                {canManage && (
                  <DataTable.HeaderCell>Actions</DataTable.HeaderCell>
                )}
              </tr>
            </DataTable.Header>
            <DataTable.Body>
              {members.map((member) => (
                <OrganizationMemberRow
                  key={member.id}
                  canManage={canManage}
                  member={member}
                  onRemove={(memberId) => void handleRemove(memberId)}
                  onRoleChange={(memberId, role) =>
                    void handleRoleChange(memberId, role)
                  }
                />
              ))}
            </DataTable.Body>
          </DataTable>
        )}
      </PanelSection>

      {/* Invite form */}
      {canManage && (
        <PanelSection title="Invite Member">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <FormInput
                label="Email"
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div>
              <FormSelect
                label="Role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                options={[...ROLE_OPTIONS]}
              />
            </div>
            <Button
              variant="primary"
              onClick={() => void handleInvite()}
              loading={inviting}
              loadingText="Inviting..."
            >
              Invite
            </Button>
          </div>
        </PanelSection>
      )}

      {canManage && (
        <OrganizationPendingInvitesSection
          invites={pendingInvites}
          actionState={inviteActionState}
          onResend={(inviteId, email, role) =>
            void handleResendInvite(inviteId, email, role)
          }
          onInvalidate={(inviteId) => void handleInvalidateInvite(inviteId)}
        />
      )}
    </div>
  );
};
